import { ChangeDetectionStrategy, Component, Injector, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { MesTeamProfileTransferService } from '@shared/services/export-import-services/mes-teamProfile';
import { ImportValidatorService } from '@shared/services/filevalidators/Importvalidator.service';
import { NotificationService } from '@shared/services/notification.service';
import { ITeamProfile, TeamProfileService } from '@shared/services/teamProfiles.service';
import { Subject, takeUntil } from 'rxjs';
import { CreateUpdateTeamProfileDialogComponent } from './create-update-team-profile/create-update-team-profile';

interface ITableViewModelItem {
	alias: string;
	description: string;
	profile: ITeamProfile;
}

@Component({
	selector: 'app-team-profiles',
	templateUrl: './team-profiles.component.html',
	styleUrls: ['./team-profiles.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class TeamProfilesComponent {
	private destroy$: Subject<void> = new Subject<void>();
	displayedColumns: string[] = ['description', 'menu'];
	dataSource = new MatTableDataSource<ITableViewModelItem>();
	showSearch = false;
	isLoading = true;

	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild(MatSort) sort: MatSort;

	favoriteItems: SubMenuItem[] = [
		{
			icon: () => 'acies_add',
			text: () => 'mes.administration.users.newbtn',
			click: () => this.openTeamProfile(null),
		},
	];

	menuItems: SubMenuItem[] = [
		{
			icon: () => 'acies_download',
			text: () => 'mes.administration.team-profiles.import.button',
			click: () => this.onImport(),
		},
		{
			icon: () => 'acies_upload',
			text: () => 'mes.administration.team-profiles.export.button',
			click: () => this.onExport(),
		},
	];

	constructor(
		public dialog: MatDialog,
		private teamProfileService: TeamProfileService,
		private teamProfileTransferService: MesTeamProfileTransferService,
		private notificationService: NotificationService,
		private injector: Injector,
	) {}

	ngAfterViewInit() {
		this.dataSource.paginator = this.paginator;
		this.dataSource.sort = this.sort;
		this.updateList();
	}

	private getITableViewModelItem(profile: ITeamProfile): ITableViewModelItem {
		return {
			alias: profile.alias,
			description: profile.description,
			profile,
		};
	}

	private getTableViewModel(teamProfiles: ITeamProfile[]): ITableViewModelItem[] {
		return teamProfiles.map((profile) => this.getITableViewModelItem(profile));
	}

	updateList() {
		this.isLoading = true;
		this.teamProfileService.getTeamProfiles().subscribe((c) => {
			this.dataSource.data = this.getTableViewModel(c.result);
			this.onSort(this.sort);
			this.isLoading = false;
		});
	}

	public onSort(sort: Sort) {
		if (sort.direction === '') {
			this.sort.sort({ id: 'alias', start: 'asc', disableClear: true });
		}
	}

	applyFilter(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement) {
			const filterValue = event.target.value;
			this.dataSource.filter = filterValue.trim().toLowerCase();
		}
	}

	openTeamProfile(teamProfile: ITeamProfile | null) {
		const dialogRef = this.dialog.open(CreateUpdateTeamProfileDialogComponent, {
			width: '800px',
			data: teamProfile,
		});
		dialogRef.componentInstance.saved.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.updateList();
		});
	}

	onExport() {
		const profile = this.dataSource.data.map((item) => item.profile);

		this.teamProfileTransferService.exportData(profile).subscribe({
			next: () => {
				this.notificationService.notify(['mes.administration.team-profiles.export.notification.success.header'], ['mes.administration.team-profiles.export.notification.success.content'], 'success');
			},
			error: (error) => {
				console.error('Failed to export data', error);
				this.notificationService.notify(['mes.administration.team-profiles.export.notification.error.header'], ['mes.administration.team-profiles.export.notification.error.content'], 'error');
			},
		});
	}

	onImport() {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'mes.administration.team-profiles.import.title',
				subHeadingKey: 'mes.administration.team-profiles.import.subtitle',
				confirmButtonKey: 'mes.administration.team-profiles.import.button',
				notificationHeader: 'mes.administration.team-profiles.import.notification.success.header',
				notificationContent: 'mes.administration.team-profiles.import.notification.success.content',
				notificationErrorHeader: 'mes.administration.team-profiles.import.notification.error.header',
				notificationErrorContent: 'mes.administration.team-profiles.import.notification.error.content',
				notificationClientErrorHeader: 'mes.administration.team-profiles.import.notification.clientError.header',
				notificationClientErrorContent: 'mes.administration.team-profiles.import.notification.clientError.content',
				detailedClientErrorMessage: 'fileuploader.detailed-client-error-message',
				allowedFileTypes: ['.json'],
				validatorService: this.injector.get(ImportValidatorService),
				data: this.teamProfileTransferService,
				optionalFields: [
					{
						name: 'overwrite',
						titleKey: 'mes.administration.team-profiles.import.overwrite',
						type: 'checkbox',
						value: false,
					},
				],
			},
		});

		dialogRef.afterClosed().subscribe(() => {
			this.updateList();
		});
	}
}

export interface WorkDay {
	dayName: string;
	startTime: string;
	endTime: string;
	breaks: [{ startTime: string; endTime: string }];
}
